import { IPlace } from '../Services/types'
import { IServicePropsWithIndex } from './types'
import { __ } from '@wordpress/i18n'
import { BookingCalendar } from '../BookingCalendar/BookingCalendar'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    convertToJSFormat,
    getCalendarMonthRange,
    wbkFormat,
} from '../../../admin/components/Form/utils/dateTime'
import {
    endOfMonth,
    endOfWeek,
    fromUnixTime,
    getUnixTime,
    startOfMonth,
    startOfWeek,
    format,
} from 'date-fns'
import { useBookingContext } from '../../providers/BookingFormProvider/BookingFormProvider'
import { Timeslots } from '../Timeslots/Timeslots'
import { useDispatch, useSelect } from '@wordpress/data'
import { store_name } from '../../../store/frontend'
import {
    wbkBackendDate,
    wbkExtractBackendDate,
} from '../../providers/BookingFormProvider/utils'
import { toZonedTime } from 'date-fns-tz'
import classNames from 'classnames'
import iconAccordionArrow from '../../../../public/images/icon-accordion-arrow.svg'
import cartReduceIcon from '../../../../public/images/icon-cart-reduce.svg'
import cartIncreaseIcon from '../../../../public/images/icon-cart-increase.svg'
import { useWording } from '../../hooks/useWording'
import { useLocale } from '../../hooks/useLocale'
import * as locales from 'date-fns/locale'

export const SelectedItem = ({
    label,
    id,
    selectedDate,
    timeslots,
    places,
    onUpdate,
    index,
    selectedMonth,
    expanded,
    quantity,
    max_quantity,
    min_quantity,
    min_slots,
    max_slots,
    consecutive_timeslots,
    group_booking,
    limited_timeslot,
    first_available,
}: IServicePropsWithIndex) => {
    const wording = useWording()
    const { loading, preset } = useBookingContext()
    const isTimeslotsLoading =
        id !== undefined && loading?.serviceTimeslots?.[id]
    const { locale } = useLocale()
    const initialized = useRef(false)

    const dateFnsLocale = useMemo(() => {
        // Helper function to get locale from the available locales
        const getLocale = (localeCode: string) => {
            // Common locale mappings
            const localeMapping: { [key: string]: string } = {
                en: 'enUS',
                'en-US': 'enUS',
                'en-GB': 'enGB',
                es: 'es',
                'es-ES': 'es',
                fr: 'fr',
                'fr-FR': 'fr',
                de: 'de',
                'de-DE': 'de',
                it: 'it',
                'it-IT': 'it',
                pt: 'pt',
                'pt-PT': 'pt',
                'pt-BR': 'ptBR',
                ru: 'ru',
                'ru-RU': 'ru',
                ja: 'ja',
                'ja-JP': 'ja',
                ko: 'ko',
                'ko-KR': 'ko',
                zh: 'zhCN',
                'zh-CN': 'zhCN',
                'zh-TW': 'zhTW',
                ar: 'ar',
                'ar-SA': 'arSA',
                nl: 'nl',
                'nl-NL': 'nl',
                sv: 'sv',
                'sv-SE': 'sv',
                da: 'da',
                'da-DK': 'da',
                no: 'nb',
                nb: 'nb',
                fi: 'fi',
                'fi-FI': 'fi',
                pl: 'pl',
                'pl-PL': 'pl',
                cs: 'cs',
                'cs-CZ': 'cs',
                sk: 'sk',
                'sk-SK': 'sk',
                hu: 'hu',
                'hu-HU': 'hu',
                ro: 'ro',
                'ro-RO': 'ro',
                bg: 'bg',
                'bg-BG': 'bg',
                hr: 'hr',
                'hr-HR': 'hr',
                sr: 'sr',
                'sr-RS': 'sr',
                sl: 'sl',
                'sl-SI': 'sl',
                et: 'et',
                'et-EE': 'et',
                lv: 'lv',
                'lv-LV': 'lv',
                lt: 'lt',
                'lt-LT': 'lt',
                tr: 'tr',
                'tr-TR': 'tr',
                he: 'he',
                'he-IL': 'he',
                hi: 'hi',
                'hi-IN': 'hi',
                th: 'th',
                'th-TH': 'th',
                vi: 'vi',
                'vi-VN': 'vi',
                id: 'id',
                'id-ID': 'id',
                ms: 'ms',
                'ms-MY': 'ms',
                bn: 'bn',
                'bn-BD': 'bn',
                'bn-IN': 'bn',
            }

            // Try exact match first
            const mappedLocale = localeMapping[localeCode]
            if (mappedLocale && locales[mappedLocale as keyof typeof locales]) {
                return locales[mappedLocale as keyof typeof locales]
            }

            // Try language code only (e.g., 'en' from 'en-US')
            const languageCode = localeCode.split('-')[0]
            const mappedLanguage = localeMapping[languageCode]
            if (
                mappedLanguage &&
                locales[mappedLanguage as keyof typeof locales]
            ) {
                return locales[mappedLanguage as keyof typeof locales]
            }

            // Default fallback
            return locales.enUS
        }

        return getLocale(locale || 'en')
    }, [locale])

    // Ref for the timeslots component
    const timeslotsRef = useRef<HTMLDivElement>(null)

    // general
    const {
        fetchServiceAvailability,
        fetchServiceTimeslots,
        fetchBookingAmounts,
    } = useDispatch(store_name)
    const { formData, dateFormat, timeFormat, timezone, attrService } =
        useBookingContext()
    const currentRange = useMemo(() => {
        let monthToSelect = selectedMonth

        // if (!initialized.current) {
        //     initialized.current = true

        //     monthToSelect = wbkExtractBackendDate(first_available || '')
        // }

        return [
            startOfWeek(startOfMonth(monthToSelect), { weekStartsOn: 0 }),
            endOfWeek(
                endOfMonth(
                    new Date(
                        monthToSelect.getFullYear(),
                        monthToSelect.getMonth() + 1,
                        1
                    )
                ),
                { weekStartsOn: 0 }
            ),
        ]
    }, [selectedMonth, first_available])

    // date updater
    const availableDates = useSelect(
        (select: any) =>
            select(store_name).getServiceAvailability(
                id,
                wbkBackendDate(currentRange[0]),
                wbkBackendDate(currentRange[1])
            ),
        [id, currentRange]
    )

    const actualSelectedDate = useMemo(() => {
        const formattedAvailableDates = availableDates.map((date: string) =>
            wbkExtractBackendDate(date)
        )

        if (!formattedAvailableDates || formattedAvailableDates.length === 0) {
            return null
        }

        // if (
        //     !formattedAvailableDates ||
        //     (formattedAvailableDates.length === 0 && !first_available)
        // ) {
        //     return null
        // } else if (formattedAvailableDates.length === 0 && first_available) {
        //     return wbkExtractBackendDate(first_available)
        // }

        const dateFound = formattedAvailableDates.find(
            (d: Date) => d.getTime() === selectedDate?.getTime()
        )

        if (dateFound) {
            return dateFound
        }

        return formattedAvailableDates[0]
    }, [selectedDate, availableDates])

    const handleSetDate = useCallback(
        (value: Date) => {
            onUpdate({ selectedDate: value })
        },
        [onUpdate, fetchServiceAvailability]
    )

    const handleSetMonth = useCallback((value: Date) => {
        onUpdate({ selectedMonth: value })
        const range = getCalendarMonthRange(value)

        fetchServiceAvailability(
            id,
            wbkBackendDate(range[0]),
            wbkBackendDate(range[1])
        )
    }, [])

    // timeslot updater
    const apiTimeSlots = useSelect(
        (select: any) =>
            select(store_name).getServiceTimeslots(
                id,
                wbkBackendDate(actualSelectedDate as Date),
                new Date().getTimezoneOffset()
            ),
        [id, actualSelectedDate]
    )

    useEffect(() => {
        onUpdate({ timeslots: apiTimeSlots })
    }, [apiTimeSlots])

    useEffect(() => {
        fetchServiceTimeslots(
            wbkBackendDate(actualSelectedDate as Date),
            id,
            new Date().getTimezoneOffset()
        )
    }, [actualSelectedDate])

    // Auto-scroll to timeslots when they finish loading
    useEffect(() => {
        if (
            !isTimeslotsLoading &&
            timeslots &&
            timeslots.length > 0 &&
            timeslotsRef.current &&
            expanded
        ) {
            // Small delay to ensure DOM is updated
            const timeoutId = setTimeout(() => {
                const timeslotsElement = timeslotsRef.current
                if (!timeslotsElement) return

                // Scroll within the specific container only
                timeslotsElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest', // This prevents body scroll
                    inline: 'nearest',
                })
            }, 100)

            return () => clearTimeout(timeoutId)
        }
    }, [isTimeslotsLoading, timeslots, expanded])

    useEffect(() => {
        if (!formData.places || Object.keys(formData.places).length === 0)
            return

        fetchBookingAmounts(formData)
    }, [formData.places])

    const handleSetTime = useCallback(
        (time: number) => {
            const minSlotsLimit = min_slots && min_slots > 0 ? min_slots : 1
            const maxSlotsLimit =
                max_slots && max_slots > 0 ? max_slots : Infinity
            const singleSlotMode = minSlotsLimit === 1 && maxSlotsLimit === 1
            const exists = places?.find(
                ({ timeslot, date }) =>
                    date.getDate() === actualSelectedDate.getDate() &&
                    date.getMonth() === actualSelectedDate.getMonth() &&
                    date.getFullYear() === actualSelectedDate.getFullYear() &&
                    timeslot === time
            )

            if (exists !== undefined) {
                onUpdate({
                    places: places?.filter(({ timeslot }) => timeslot !== time),
                })
                return
            }

            if (singleSlotMode) {
                onUpdate({
                    places: [
                        {
                            date: actualSelectedDate as Date,
                            timeslot: time,
                        },
                    ],
                })
            } else {
                onUpdate({
                    places: [
                        ...(places as IPlace[]),
                        {
                            date: actualSelectedDate as Date,
                            timeslot: time,
                        },
                    ],
                })
            }
        },
        [timeslots, actualSelectedDate, places, min_slots, max_slots]
    )

    const { services } = useBookingContext()
    const isDisabled = useMemo(() => {
        const selectedServices = services
            .filter(({ selected }) => selected)
            .sort((a, b) => (a.selectedAt || 0) - (b.selectedAt || 0))

        if (selectedServices.length === 1) return false

        const currentIndex = selectedServices.findIndex(
            (service) => service.id === id
        )

        return selectedServices[currentIndex - 1]?.places?.length === 0
    }, [services, id])

    const setQuantity = useCallback(
        (updatedQuantity: number) => {
            if (
                (min_quantity && updatedQuantity < min_quantity) ||
                (max_quantity && updatedQuantity > max_quantity)
            )
                return

            onUpdate({
                quantity: updatedQuantity,
            })
        },
        [quantity, min_quantity, max_quantity]
    )

    // --- Begin: Manual quantity input state and handler ---
    const [manualQuantity, setManualQuantity] = useState<number | string>(
        quantity || min_quantity || 1
    )

    useEffect(() => {
        // Keep manualQuantity in sync with prop changes
        setManualQuantity(quantity || min_quantity || 1)
    }, [quantity, min_quantity])

    const handleQuantityInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        // Allow empty string for typing
        if (value === '') {
            setManualQuantity('')
            return
        }
        let num = parseInt(value, 10)
        if (isNaN(num)) {
            setManualQuantity('')
            return
        }
        // Clamp to min/max
        if (min_quantity && num < min_quantity) num = min_quantity
        if (max_quantity && num > max_quantity) num = max_quantity
        setManualQuantity(num)
        setQuantity(num)
    }

    const handleQuantityInputBlur = () => {
        // If left empty, reset to min or 1
        if (
            manualQuantity === '' ||
            manualQuantity === undefined ||
            manualQuantity === null
        ) {
            setManualQuantity(min_quantity || 1)
            setQuantity(min_quantity || 1)
        }
    }
    // --- End: Manual quantity input state and handler ---

    const currentHiddenSlots = useMemo(
        () => timeslots?.filter(({ free_places }) => free_places === 0) || [],
        [timeslots]
    )
    const currentFreeSlots = useMemo(
        () => timeslots?.filter(({ free_places }) => free_places > 0) || [],
        [timeslots]
    )

    return (
        <div
            className={classNames('wbk_selected_services__item', {
                ['wbk_selected_services__item--expanded']: expanded,
                ['wbk_selected_services__item--disabled']: isDisabled,
            })}
        >
            <div
                className={'wbk_selected_services__item__heading'}
                onClick={() => !isDisabled && onUpdate({ expanded: !expanded })}
            >
                <div className={'wbk_selected_services__item__heading__index'}>
                    {index + 1}
                </div>
                <p className={'wbk_selected_services__item__heading__title'}>
                    {label}
                </p>
                <div
                    className={
                        'wbk_selected_services__item__heading__selected-times'
                    }
                >
                    {(places &&
                        places.length > 0 &&
                        places.map(({ timeslot }: IPlace) => (
                            <div
                                className={
                                    'wbk_selected_services__item__heading__selected-timeslot'
                                }
                            >
                                <div>{wbkFormat(timeslot, dateFormat)}</div>
                                <span></span>
                                <div>
                                    {wbkFormat(
                                        getUnixTime(
                                            toZonedTime(
                                                fromUnixTime(timeslot),
                                                timezone
                                            )
                                        ),
                                        timeFormat
                                    )}
                                </div>
                            </div>
                        ))) || (
                        <div
                            className={
                                'wbk_selected_services__item__heading__empty-timeslots-label'
                            }
                        >
                            {wording.select_date_time ||
                                __('Select date & time', 'webba-booking-lite')}
                        </div>
                    )}
                </div>
                <div>
                    <img
                        className={
                            'wbk_selected_services__item__heading__toggle-icon'
                        }
                        src={iconAccordionArrow}
                        alt={
                            wording.toggle || __('Toggle', 'webba-booking-lite')
                        }
                    />
                </div>
            </div>
            <div className={'wbk_selected_services__item__body'}>
                <BookingCalendar
                    availableDates={availableDates.map((date: string) =>
                        wbkExtractBackendDate(date)
                    )}
                    selectedDate={actualSelectedDate as Date}
                    setValue={handleSetDate}
                    setMonth={handleSetMonth}
                />
                {max_quantity &&
                    max_quantity > 1 &&
                    attrService !== null &&
                    attrService !== '0' &&
                    attrService && (
                        <div
                            className={
                                'wbk_selected_services__item__bottom-part'
                            }
                        >
                            <div
                                className={
                                    'wbk_selected_services__item__bottom-part__group-heading'
                                }
                            >
                                <p
                                    className={
                                        'wbk_selected_services__item__bottom-part__bottom-heading'
                                    }
                                >
                                    {wording?.for_group_bookings_only ||
                                        __(
                                            'For group bookings only:',
                                            'webba-booking-lite'
                                        )}
                                </p>
                                <span>
                                    {wording.number_of_people ||
                                        __(
                                            'Number of people:',
                                            'webba-booking-lite'
                                        )}
                                </span>
                            </div>
                            <div
                                className={
                                    'wbk_selected_services__item__bottom-part__quantity-wrapper'
                                }
                            >
                                <div
                                    className={classNames(
                                        'wbk_selected_services__item__bottom-part__quantity-button',
                                        'wbk_selected_services__item__bottom-part__quantity-button--reduce'
                                    )}
                                    onClick={(e: any) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setQuantity(
                                            (Number(quantity) ||
                                                min_quantity ||
                                                1) - 1
                                        )
                                    }}
                                >
                                    <img
                                        src={cartReduceIcon}
                                        alt={
                                            wording.reduce_item ||
                                            __(
                                                'Reduce item',
                                                'webba-booking-lite'
                                            )
                                        }
                                    />
                                </div>
                                <input
                                    type="number"
                                    value={manualQuantity}
                                    min={min_quantity || 1}
                                    max={max_quantity || 1}
                                    className={
                                        'wbk_selected_services__item__bottom-part__quantity-input'
                                    }
                                    onChange={handleQuantityInputChange}
                                    onBlur={handleQuantityInputBlur}
                                    onClick={(e: any) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                    }}
                                />
                                <div
                                    className={classNames(
                                        'wbk_selected_services__item__bottom-part__quantity-button',
                                        'wbk_selected_services__item__bottom-part__quantity-button--increase'
                                    )}
                                    onClick={(e: any) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setQuantity(
                                            (Number(quantity) ||
                                                min_quantity ||
                                                1) + 1
                                        )
                                    }}
                                >
                                    <img
                                        src={cartIncreaseIcon}
                                        alt={
                                            wording.increase_item ||
                                            __(
                                                'Increase item',
                                                'webba-booking-lite'
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                {actualSelectedDate && (
                    <div
                        className={'wbk_selected_services__item__selected-date'}
                    >
                        <p>
                            {wording?.choosing_timeslot_for ||
                                __(
                                    'Choosing time slot for ',
                                    'webba-booking-lite'
                                )}{' '}
                            <span>
                                {format(
                                    actualSelectedDate as Date,
                                    convertToJSFormat(dateFormat || 'PPP'),
                                    { locale: dateFnsLocale }
                                )}
                            </span>
                        </p>
                    </div>
                )}
                {timeslots &&
                    !(
                        preset?.settings?.show_booked_slots === false &&
                        currentFreeSlots.length === 0
                    ) && (
                        <div ref={timeslotsRef}>
                            <Timeslots
                                timeslots={timeslots || {}}
                                selectedSlots={
                                    places
                                        ?.filter(({ date }) => {
                                            return (
                                                date !== null &&
                                                actualSelectedDate !== null &&
                                                date.getDate() ===
                                                    actualSelectedDate.getDate() &&
                                                date.getMonth() ===
                                                    actualSelectedDate.getMonth() &&
                                                date.getFullYear() ===
                                                    actualSelectedDate.getFullYear()
                                            )
                                        })
                                        .map(({ timeslot }) => timeslot) || []
                                }
                                setSlot={handleSetTime}
                                max_quantity={Number(max_quantity)}
                                quantity={quantity}
                                serviceId={id}
                                min_slots={min_slots}
                                max_slots={max_slots}
                                consecutive_timeslots={consecutive_timeslots}
                                places={places as IPlace[]}
                                group_booking={group_booking}
                                limited_timeslot={limited_timeslot}
                            />
                        </div>
                    )}
                {actualSelectedDate &&
                    !isTimeslotsLoading &&
                    (!timeslots ||
                        timeslots.length === 0 ||
                        (currentFreeSlots.length === 0 &&
                            currentHiddenSlots.length > 0)) && (
                        <div
                            className={
                                'wbk_selected_services__item__no-timeslots'
                            }
                        >
                            {wording?.no_available_timeslots ||
                                __(
                                    'No available time slots',
                                    'webba-booking-lite'
                                )}
                        </div>
                    )}
            </div>
        </div>
    )
}
